import React, { useEffect, useState, useRef } from 'react';
import LoadError from '../../components/LoadError';
import { formatDate } from '../../lib/utils';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDelete, apiUpload } from '../../contexts/AuthContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DOC_TYPES_BACHELORS, DOC_TYPES_MASTERS_PHD, docTypeLabel, checklistItem } from './CandidateDetail';
import DocumentPreview, { downloadDocument } from '../../components/DocumentPreview';
import { docTypeNameProblem, expectedTypeFileName } from '../../lib/docNaming';
import CourseCombobox from '../../components/CourseCombobox';
import HandoffPanel from '../../components/passport/HandoffPanel';
import {
  ArrowLeft, Building2, User, Calendar, MessageSquare, Send, Trash2,
  Clock, FileText, ChevronDown, Shield, Briefcase, ExternalLink,
  Upload, Download, BookOpen, GraduationCap, ClipboardCheck, Eye, Info
} from 'lucide-react';
import { toast } from 'sonner';

import { APPLICATION_STATUSES as STATUSES, APP_STATUS_STYLES as STATUS_STYLES } from '../../components/WorkflowTracker';

const APP_DOC_TYPES = [
  { value: 'offer_letter',         label: 'Offer Letter' },
  { value: 'conditional_offer',    label: 'Conditional Offer' },
  { value: 'unconditional_offer',  label: 'Unconditional Offer' },
  { value: 'rejection_letter',     label: 'Rejection Letter' },
  { value: 'i20',                  label: 'I-20' },
  { value: 'visa',                 label: 'Visa' },
  { value: 'scholarship_letter',   label: 'Scholarship Letter' },
  { value: 'other',                label: 'Other' },
];

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function fmtDateShort(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function ApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const basePath = isAdmin ? '/admin' : '/partner';
  const commentsEndRef = useRef(null);

  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [editingDetails, setEditingDetails] = useState(false);
  const [editCourseName, setEditCourseName] = useState('');
  const [editIntake, setEditIntake] = useState('');
  const [savingDetails, setSavingDetails] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [appDocs, setAppDocs] = useState([]);
  const [docType, setDocType] = useState(isAdmin ? 'offer_letter' : 'other');
  const [docUploading, setDocUploading] = useState(false);
  const docFileRef = useRef(null);
  // The candidate's own required-document checklist (10th certificate,
  // passport, SOP, etc. — uploaded from the Candidate profile page), shown
  // here read-only so reviewing an application doesn't require flipping
  // back to the candidate's page just to check what's been submitted.
  const [candidateProfileType, setCandidateProfileType] = useState('masters');
  const [candidateDocs, setCandidateDocs] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(null);

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    apiGet(`/applications/${id}`)
      .then(r => {
        setApp(r.data);
        const candidateId = r.data.candidate_id;
        if (candidateId) {
          apiGet(`/candidates/${candidateId}`)
            .then(cr => setCandidateProfileType(cr.data.profile_type || 'masters'))
            .catch(() => {});
          apiGet(`/candidates/${candidateId}/documents`)
            .then(dr => setCandidateDocs(dr.data))
            .catch(() => {});
        }
      })
      .catch(err => {
        // Only a missing/forbidden application sends the user back; a network error offers Retry instead.
        if ([403, 404].includes(err.response?.status)) { toast.error('Application not found'); navigate(`${basePath}/applications`); }
        else setLoadError(true);
      })
      .finally(() => setLoading(false));
    apiGet(`/applications/${id}/documents`)
      .then(r => setAppDocs(r.data))
      .catch(() => {});
  }, [id, basePath, navigate, attempt]);

  const handleEditDetails = () => {
    setEditCourseName(app.course_name || '');
    setEditIntake(app.intake || '');
    setEditingDetails(true);
  };

  const handleSaveDetails = async () => {
    setSavingDetails(true);
    try {
      const res = await apiPut(`/applications/${id}`, { course_name: editCourseName, intake: editIntake });
      setApp(res.data);
      setEditingDetails(false);
      toast.success('Details updated');
    } catch { toast.error('Failed to update'); }
    finally { setSavingDetails(false); }
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const problem = docTypeNameProblem(file.name, docType);
    if (problem) {
      toast.error(problem, { duration: 8000 });
      if (docFileRef.current) docFileRef.current.value = '';
      return;
    }
    setDocUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiUpload(`/applications/${id}/documents?doc_type=${docType}`, formData);
      setAppDocs(prev => [...prev, res.data]);
      toast.success('Document uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setDocUploading(false);
      if (docFileRef.current) docFileRef.current.value = '';
    }
  };

  const handleDocDownload = (doc) => downloadDocument(doc);

  const handleDocDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await apiDelete(`/documents/${docId}`);
      setAppDocs(prev => prev.filter(d => d.id !== docId));
      toast.success('Document deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      const res = await apiPut(`/applications/${id}`, { status: newStatus });
      setApp(res.data);
      toast.success('Status updated');
    } catch { toast.error('Failed to update status'); }
    finally { setUpdatingStatus(false); }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    setSubmittingComment(true);
    try {
      const res = await apiPost(`/applications/${id}/comments`, { text: commentText.trim() });
      setApp(prev => ({ ...prev, comments: [...(prev.comments || []), res.data] }));
      setCommentText('');
      setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch { toast.error('Failed to add comment'); }
    finally { setSubmittingComment(false); }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    setDeletingCommentId(commentId);
    try {
      await apiDelete(`/applications/${id}/comments/${commentId}`);
      setApp(prev => ({ ...prev, comments: (prev.comments || []).filter(c => c.id !== commentId) }));
    } catch { toast.error('Failed to delete comment'); }
    finally { setDeletingCommentId(null); }
  };

  const canDeleteComment = (comment) => {
    if (isAdmin) return true;
    return comment.author_id === user?.id || comment.author_id === user?._id;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-3 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (loadError) return <LoadError what="this application" onRetry={() => setAttempt(a => a + 1)} />;
  if (!app) return null;

  const comments = app.comments || [];

  // Candidate's required-document checklist, same rule CandidateDetail.js
  // uses: documents uploaded from an application context (they carry an
  // application_id) are excluded, so this mirrors exactly what shows as
  // "Required Documents" on the candidate's own profile page.
  const candidateChecklistDocs = candidateDocs.filter(d => !d.application_id);
  const candidateDocTypes = candidateProfileType === 'bachelors' ? DOC_TYPES_BACHELORS : DOC_TYPES_MASTERS_PHD;
  const getCandidateDocTypeLabel = (value) => docTypeLabel(candidateDocTypes, value);

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(`${basePath}/applications`)}
          aria-label="Back to applications"
          className="p-2 rounded-lg hover:bg-contrast/[0.07] transition-colors text-ink-2 hover:text-ink">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-semibold text-ink tracking-tight" style={{ fontFamily: 'Outfit' }}>
              Application Details
            </h1>
            <span className="font-mono text-sm font-bold text-brand bg-surface-2 px-3 py-1 rounded-lg">
              {app.application_number}
            </span>
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_STYLES[app.status] || 'bg-contrast/5 text-ink-2'}`}>
              {app.status}
            </span>
          </div>
          <p className="text-ink-2 text-sm mt-0.5">
            Created {fmtDateShort(app.created_at)}
          </p>
        </div>
      </div>

      <HandoffPanel application={app} isAdmin={isAdmin} onUpdate={setApp} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Application info card */}
          <div className="bg-surface/70 backdrop-blur-xl rounded-2xl border border-contrast/10 shadow-card p-6">
            <h2 className="text-base font-semibold text-ink mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4 text-brand" />
              Application Info
            </h2>
            <div className="space-y-4">
              {/* Candidate */}
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-surface-3 flex items-center justify-center shrink-0 mt-0.5">
                  <User className="w-4 h-4 text-brand" />
                </div>
                <div>
                  <p className="text-xs text-ink-3 font-medium uppercase tracking-wide">Candidate</p>
                  <Link
                    to={`${basePath}/candidates/${app.candidate_id}`}
                    className="text-sm font-semibold text-brand hover:text-brand-soft flex items-center gap-1 mt-0.5">
                    {app.candidate_name}
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              </div>

              {/* University */}
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Building2 className="w-4 h-4 text-success" />
                </div>
                <div>
                  <p className="text-xs text-ink-3 font-medium uppercase tracking-wide">University</p>
                  <p className="text-sm font-semibold text-ink mt-0.5">{app.university_name}</p>
                  <p className="text-xs text-ink-2">{app.university_country}</p>
                </div>
              </div>

              {/* Course & Intake */}
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center shrink-0 mt-0.5">
                  <BookOpen className="w-4 h-4 text-brand-soft" />
                </div>
                <div className="flex-1">
                  {editingDetails ? (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-ink-3 font-medium uppercase tracking-wide mb-1">Course</p>
                          <CourseCombobox
                            value={editCourseName}
                            onChange={setEditCourseName}
                            params={app.university_id ? { university_id: app.university_id } : {}}
                            placeholder="Select course..."
                            className="px-2.5 py-1.5"
                          />
                        </div>
                        <div>
                          <p className="text-xs text-ink-3 font-medium uppercase tracking-wide mb-1">Intake</p>
                          <input
                            value={editIntake}
                            onChange={e => setEditIntake(e.target.value)}
                            placeholder="e.g. Sep 2025"
                            className="w-full rounded-md border border-contrast/10 bg-surface text-ink placeholder:text-ink-3 px-2.5 py-1.5 text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleSaveDetails} disabled={savingDetails} className="bg-brand hover:bg-brand-soft text-white text-xs h-7 px-3">
                          {savingDetails ? 'Saving…' : 'Save'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingDetails(false)} className="text-xs h-7 px-3">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-x-8 gap-y-1">
                      <div>
                        <p className="text-xs text-ink-3 font-medium uppercase tracking-wide">Course</p>
                        <p className="text-sm font-semibold text-ink mt-0.5">{app.course_name || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-ink-3 font-medium uppercase tracking-wide">Intake</p>
                        <p className="text-sm font-semibold text-ink mt-0.5">{app.intake || '—'}</p>
                      </div>
                      <div className="col-span-2 mt-1">
                        <button onClick={handleEditDetails} className="text-xs text-brand hover:text-brand-soft hover:underline">
                          Edit
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Partner (admin only) */}
              {isAdmin && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-surface-3 flex items-center justify-center shrink-0 mt-0.5">
                    <Briefcase className="w-4 h-4 text-ember" />
                  </div>
                  <div>
                    <p className="text-xs text-ink-3 font-medium uppercase tracking-wide">Partner / Agent</p>
                    <p className="text-sm font-semibold text-ink mt-0.5">{app.partner_name || '—'}</p>
                  </div>
                </div>
              )}

              {/* Dates */}
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-contrast/5 flex items-center justify-center shrink-0 mt-0.5">
                  <Calendar className="w-4 h-4 text-ink-2" />
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1">
                  <div>
                    <p className="text-xs text-ink-3 font-medium uppercase tracking-wide">Applied On</p>
                    <p className="text-sm text-ink mt-0.5">{fmtDateShort(app.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-ink-3 font-medium uppercase tracking-wide">Last Updated</p>
                    <p className="text-sm text-ink mt-0.5">{fmtDateShort(app.updated_at)}</p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {app.notes && (
                <div className="pt-2 border-t border-contrast/[0.07]">
                  <p className="text-xs text-ink-3 font-medium uppercase tracking-wide mb-1">Notes</p>
                  <p className="text-sm text-ink whitespace-pre-wrap">{app.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Documents card */}
          <div className="bg-surface/70 backdrop-blur-xl rounded-2xl border border-contrast/10 shadow-card p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h2 className="text-base font-semibold text-ink flex items-center gap-2">
                  <Upload className="w-4 h-4 text-brand" />
                  Application Documents
                </h2>
                <div className="flex flex-wrap items-center gap-2">
                  {isAdmin && (
                    <select
                      value={docType}
                      onChange={e => setDocType(e.target.value)}
                      className="min-w-0 max-w-full text-xs border border-contrast/10 rounded-lg px-2 py-1.5 text-ink bg-surface focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15">
                      {APP_DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  )}
                  <Button
                    size="sm"
                    disabled={docUploading}
                    onClick={() => docFileRef.current?.click()}
                    className="bg-brand hover:bg-brand-soft text-white gap-1.5 text-xs">
                    <Upload className="w-3.5 h-3.5" />
                    {docUploading ? 'Uploading…' : 'Upload'}
                  </Button>
                  <input ref={docFileRef} type="file" className="hidden" onChange={handleDocUpload} />
                </div>
              </div>
              <div className="mb-3 flex items-start gap-2 p-3 rounded-lg border border-brand/30 bg-surface-3/60 text-xs text-ink-2">
                <Info className="w-4 h-4 text-brand shrink-0 mt-0.5" />
                {docType === 'other' ? (
                  <span>Documents of type <span className="font-semibold text-ink">Other</span> can have any file name.</span>
                ) : (
                  <span>
                    Before uploading, name each file after its <span className="font-semibold text-ink">document type</span>, e.g.{' '}
                    <span className="font-mono text-ink">{expectedTypeFileName(docType)}.pdf</span>.
                    {' '}Files named any other way are not accepted.
                  </span>
                )}
              </div>
              {appDocs.length === 0 ? (
                <button type="button"
                  onClick={() => docFileRef.current?.click()}
                  className="w-full border-2 border-dashed border-contrast/10 rounded-xl p-8 text-center cursor-pointer hover:border-brand hover:bg-brand/5 transition-colors">
                  <Upload className="w-8 h-8 text-ink-3 mx-auto mb-2" />
                  <p className="text-sm text-ink-3">No documents yet. Click to upload.</p>
                </button>
              ) : (
                <div className="space-y-2">
                  {appDocs.map(doc => {
                    const docLabel = APP_DOC_TYPES.find(t => t.value === doc.doc_type)?.label || doc.doc_type;
                    return (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-contrast/5 rounded-lg hover:bg-contrast/[0.07] transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="w-5 h-5 text-brand shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-ink truncate">{doc.original_filename}</p>
                            <p className="text-xs text-ink-3">{docLabel} · {doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : ''} · {formatDate(doc.created_at)}</p>
                          </div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <button onClick={() => setPreviewDoc(doc)} title="Preview" className="p-2 text-ink-2 hover:text-brand hover:bg-contrast/[0.06] rounded-lg">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDocDownload(doc)} title="Download" className="p-2 text-ink-2 hover:text-brand hover:bg-contrast/[0.06] rounded-lg">
                            <Download className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDocDelete(doc.id)} title="Delete" aria-label="Delete document" className="p-2 text-ink-2 hover:text-danger hover:bg-contrast/[0.06] rounded-lg">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
          </div>

          {/* Candidate's Required Documents (read-only mirror of the
              checklist on the candidate's own profile page) */}
          <div className="bg-surface/70 backdrop-blur-xl rounded-2xl border border-contrast/10 shadow-card p-6">
            <h2 className="text-base font-semibold text-ink mb-1 flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4 text-brand" />
              Candidate's Required Documents
              <span className="ml-1 whitespace-nowrap text-xs bg-surface-2 text-brand px-2 py-0.5 rounded-full font-semibold">
                {candidateDocTypes.filter(dt => checklistItem(candidateChecklistDocs, dt).done).length}/{candidateDocTypes.length}
              </span>
            </h2>
            <p className="text-xs text-ink-3 mb-4">
              What this candidate has submitted on their profile — upload or manage these from{' '}
              <Link to={`${basePath}/candidates/${app.candidate_id}`} className="text-brand hover:underline">the candidate's page</Link>.
            </p>

            <div className="mb-4 p-4 bg-contrast/5 rounded-lg border border-contrast/10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6">
                {candidateDocTypes.map((dt, idx) => {
                  const { count, done: isUploaded } = checklistItem(candidateChecklistDocs, dt);
                  return (
                    <div key={dt.value} className="flex items-center gap-2 text-sm">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${isUploaded ? 'bg-brand/15 text-success' : 'bg-contrast/10 text-ink-2'}`}>
                        {isUploaded ? '✓' : idx + 1}
                      </span>
                      <span className={`flex-1 ${isUploaded ? 'text-ink' : 'text-ink-2'}`}>{dt.label}</span>
                      {dt.min ? (
                        <span className="text-xs bg-surface-3 text-brand px-1.5 py-0.5 rounded font-medium">{count}/{dt.min}</span>
                      ) : count > 1 && <span className="text-xs bg-surface-3 text-brand px-1.5 py-0.5 rounded font-medium">{count}</span>}
                    </div>
                  );
                })}
              </div>
            </div>

            {candidateChecklistDocs.length > 0 && (
              <div className="space-y-2">
                {candidateChecklistDocs.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-contrast/5 rounded-lg hover:bg-contrast/[0.07] transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="w-5 h-5 text-brand shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-ink truncate">{doc.original_filename}</p>
                        <p className="text-xs text-ink-3">{getCandidateDocTypeLabel(doc.doc_type)} &middot; {doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : ''} &middot; {formatDate(doc.created_at)}</p>
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => setPreviewDoc(doc)} title="Preview" className="p-2 text-ink-2 hover:text-brand hover:bg-contrast/[0.06] rounded-lg">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDocDownload(doc)} title="Download" className="p-2 text-ink-2 hover:text-brand hover:bg-contrast/[0.06] rounded-lg">
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Comments card */}
          <div className="bg-surface/70 backdrop-blur-xl rounded-2xl border border-contrast/10 shadow-card p-6">
            <h2 className="text-base font-semibold text-ink mb-4 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-brand" />
              Comments
              {comments.length > 0 && (
                <span className="ml-1 text-xs bg-surface-2 text-brand px-2 py-0.5 rounded-full font-semibold">
                  {comments.length}
                </span>
              )}
            </h2>

            {/* Comment list */}
            {comments.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="w-8 h-8 text-ink-3 mx-auto mb-2" />
                <p className="text-sm text-ink-3">No comments yet. Be the first to add one.</p>
              </div>
            ) : (
              <div className="space-y-3 mb-5">
                {comments.map(c => (
                  <div key={c.id} className="flex gap-3 group">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                      c.role === 'admin' ? 'bg-surface-3' : 'bg-brand/10'
                    }`}>
                      {c.role === 'admin'
                        ? <Shield className="w-4 h-4 text-brand" />
                        : <Briefcase className="w-4 h-4 text-success" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-semibold text-ink">{c.author_name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide ${
                          c.role === 'admin'
                            ? 'bg-surface-2 text-brand'
                            : 'bg-brand/10 text-success'
                        }`}>
                          {c.role === 'admin' ? 'Admin' : 'Partner'}
                        </span>
                        <span className="text-xs text-ink-3">{fmtDate(c.created_at)}</span>
                        {canDeleteComment(c) && (
                          <button
                            onClick={() => handleDeleteComment(c.id)}
                            disabled={deletingCommentId === c.id}
                            aria-label="Delete comment"
                            className="ml-auto md:opacity-0 md:group-hover:opacity-100 focus-visible:opacity-100 p-1 text-ink-3 hover:text-danger hover:bg-ember/10 rounded transition-all">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-ink whitespace-pre-wrap leading-relaxed">{c.text}</p>
                    </div>
                  </div>
                ))}
                <div ref={commentsEndRef} />
              </div>
            )}

            {/* Add comment form */}
            <div className="border-t border-contrast/[0.07] pt-4">
              <Textarea
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleAddComment();
                }}
                placeholder="Add a comment… (Ctrl+Enter to submit)"
                className="resize-none text-sm border-contrast/10 focus:border-brand min-h-[80px]"
                rows={3}
              />
              <div className="flex justify-end mt-2">
                <Button
                  onClick={handleAddComment}
                  disabled={!commentText.trim() || submittingComment}
                  className="bg-brand hover:bg-brand-soft text-white h-9 px-4 text-sm flex items-center gap-2">
                  <Send className="w-3.5 h-3.5" />
                  {submittingComment ? 'Posting…' : 'Post Comment'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Right: status panel */}
        <div className="space-y-4">
          {/* Status card */}
          <div className="bg-surface/70 backdrop-blur-xl rounded-2xl border border-contrast/10 shadow-card p-5">
            <h2 className="text-sm font-semibold text-ink mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-ink-2" />
              Status
            </h2>
            {isAdmin ? (
              <div className="space-y-2">
                <p className="text-xs text-ink-3">Change status</p>
                <div className="relative">
                  <select
                    value={app.status}
                    disabled={updatingStatus}
                    onChange={e => handleStatusChange(e.target.value)}
                    className={`w-full appearance-none pr-8 pl-3 py-2 rounded-lg text-sm font-medium border cursor-pointer ${STATUS_STYLES[app.status] || 'bg-contrast/5 text-ink-2 border-contrast/10'} disabled:opacity-60`}>
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" />
                </div>
                {updatingStatus && (
                  <p className="text-xs text-ink-3 flex items-center gap-1">
                    <span className="inline-block w-3 h-3 border border-brand border-t-transparent rounded-full animate-spin" />
                    Updating…
                  </p>
                )}
              </div>
            ) : (
              <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium ${STATUS_STYLES[app.status] || 'bg-contrast/5 text-ink-2'}`}>
                {app.status}
              </span>
            )}
          </div>

          {/* Timeline card */}
          <div className="bg-surface/70 backdrop-blur-xl rounded-2xl border border-contrast/10 shadow-card p-5">
            <h2 className="text-sm font-semibold text-ink mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4 text-ink-2" />
              Timeline
            </h2>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-ink-3 uppercase tracking-wide font-medium">Created</p>
                <p className="text-sm text-ink mt-0.5">{fmtDate(app.created_at)}</p>
              </div>
              <div>
                <p className="text-xs text-ink-3 uppercase tracking-wide font-medium">Last Updated</p>
                <p className="text-sm text-ink mt-0.5">{fmtDate(app.updated_at)}</p>
              </div>
              <div>
                <p className="text-xs text-ink-3 uppercase tracking-wide font-medium">Comments</p>
                <p className="text-sm text-ink mt-0.5">{comments.length} comment{comments.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </div>

          {/* Quick links card */}
          <div className="bg-surface/70 backdrop-blur-xl rounded-2xl border border-contrast/10 shadow-card p-5">
            <h2 className="text-sm font-semibold text-ink mb-3">Quick Links</h2>
            <div className="space-y-2">
              <Link
                to={`${basePath}/candidates/${app.candidate_id}`}
                className="flex items-center gap-2 text-sm text-brand hover:text-brand-soft hover:bg-surface-2 px-2 py-1.5 rounded-lg transition-colors">
                <User className="w-3.5 h-3.5" />
                View Candidate Profile
                <ExternalLink className="w-3 h-3 ml-auto" />
              </Link>
              <Link
                to={`${basePath}/applications`}
                className="flex items-center gap-2 text-sm text-ink-2 hover:text-ink hover:bg-contrast/5 px-2 py-1.5 rounded-lg transition-colors">
                <FileText className="w-3.5 h-3.5" />
                All Applications
                <ExternalLink className="w-3 h-3 ml-auto" />
              </Link>
            </div>
          </div>
        </div>
      </div>
      <DocumentPreview doc={previewDoc} onClose={() => setPreviewDoc(null)} />
    </div>
  );
}
